import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import FilterBar, { type FilterState } from '@/components/feature/FilterBar';
import Pagination from '@/components/feature/Pagination';
import VideoCard from '@/components/feature/VideoCard';
import EmptyState from '@/components/feature/EmptyState';
import { categories } from '@/mocks/categories';
import { videos } from '@/mocks/videos';
import type { Video } from '@/types/video';
import { filterVideos, paginate } from '@/utils/filter';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const PAGE_SIZE = 6;

export default function CategoryFilterPage() {
  const { categoryId = '' } = useParams();
  const category = categories.find((c) => c.id === categoryId);
  const allVideos = videos as Video[];

  const categoryVideos = useMemo(
    () => allVideos.filter((v) => v.category === categoryId),
    [allVideos, categoryId],
  );

  const tagList = useMemo(() => {
    const set = new Set<string>();
    categoryVideos.forEach((v) => v.tags.forEach((t) => set.add(t)));
    return Array.from(set);
  }, [categoryVideos]);

  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [filters, setFilters] = useState<FilterState>({ region: 'all', duration: 'all', time: 'all' });
  const [sort, setSort] = useState<'latest' | 'hot'>('latest');
  const [page, setPage] = useState(1);

  useDocumentTitle({
    title: category ? `${category.name}筛选 - 成人快手` : '分类筛选 - 成人快手',
    description: category
      ? `按标签、地区、时长与发布时间筛选成人快手${category.name}分类下的短视频内容。`
      : '成人快手分类筛选。',
    keywords: '成人快手,分类筛选,标签筛选,短视频',
  });

  useEffect(() => {
    setSelectedTags([]);
    setFilters({ region: 'all', duration: 'all', time: 'all' });
    setSort('latest');
    setPage(1);
  }, [categoryId]);

  const filtered = useMemo(() => {
    const base = filterVideos(categoryVideos, { ...filters, sort });
    if (selectedTags.length === 0) return base;
    return base.filter((v) => selectedTags.some((t) => v.tags.includes(t)));
  }, [categoryVideos, filters, sort, selectedTags]);

  useEffect(() => {
    setPage(1);
  }, [filters, sort, selectedTags]);

  const { items, totalPages } = paginate(filtered, page, PAGE_SIZE);
  const activeCount =
    [filters.region, filters.duration, filters.time].filter((v) => v !== 'all').length +
    selectedTags.length;

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const resetAll = () => {
    setSelectedTags([]);
    setFilters({ region: 'all', duration: 'all', time: 'all' });
  };

  if (!category) {
    return (
      <div className="w-full px-4 py-16 md:px-8">
        <EmptyState
          icon="ri-emotion-sad-line"
          title="分类不存在"
          description="返回首页看看其他精彩内容吧。"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-8 md:px-8 md:py-10">
      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: category.name, to: `/category/${category.id}` },
          { label: '筛选' },
        ]}
      />

      <div className="mt-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {category.name} · 筛选与标签
          </h1>
          <p className="mt-1 text-sm text-foreground-500">
            用标签与条件组合，精准找到你想看的内容
          </p>
        </div>
        <Link
          to={`/category/${category.id}`}
          className="flex items-center gap-1 rounded-full border border-background-300/70 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-600 hover:text-primary-300"
        >
          <i className="ri-arrow-left-line" />
          返回分类页
        </Link>
      </div>

      {/* Tag cloud */}
      <div className="mt-6 rounded-lg border border-background-200/70 bg-background-100 p-4">
        <p className="mb-3 flex items-center gap-2 text-sm font-medium text-foreground-900">
          <i className="ri-price-tag-3-line text-accent-400" />
          热门标签
        </p>
        <div className="flex flex-wrap gap-2">
          {tagList.map((tag) => (
            <button
              key={tag}
              type="button"
              onClick={() => toggleTag(tag)}
              className={`rounded-full px-3 py-1.5 text-xs transition-colors ${
                selectedTags.includes(tag)
                  ? 'bg-accent-500 font-medium text-foreground-950'
                  : 'bg-background-200/70 text-foreground-700 hover:bg-background-300/60'
              }`}
            >
              #{tag}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4">
        <FilterBar value={filters} onChange={setFilters} onReset={resetAll} activeCount={activeCount} />
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-foreground-500">
          共 <span className="font-semibold text-foreground-900">{filtered.length}</span> 个视频
        </p>
        <div className="flex rounded-full border border-background-300/60 bg-background-100 p-1">
          <button
            type="button"
            onClick={() => setSort('latest')}
            className={`rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${
              sort === 'latest' ? 'bg-primary-500 text-foreground-950' : 'text-foreground-600 hover:text-foreground-950'
            }`}
          >
            最新
          </button>
          <button
            type="button"
            onClick={() => setSort('hot')}
            className={`rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${
              sort === 'hot' ? 'bg-primary-500 text-foreground-950' : 'text-foreground-600 hover:text-foreground-950'
            }`}
          >
            最热
          </button>
        </div>
      </div>

      <div className="mt-6">
        {items.length === 0 ? (
          <EmptyState
            icon="ri-filter-off-line"
            title="暂无符合条件的视频"
            description="当前标签与条件组合下没有内容，试试重置筛选。"
            actionLabel="重置筛选"
            onAction={resetAll}
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((v) => (
              <VideoCard key={v.id} video={v} />
            ))}
          </div>
        )}
      </div>

      {items.length > 0 && <Pagination current={page} total={totalPages} onChange={setPage} />}
    </div>
  );
}